import {
  createUnifiedTheme,
  defaultComponentThemes,
  defaultTypography,
  palettes,
  type UnifiedTheme,
} from '@backstage/theme';
import {
  DARK,
  DARK_STATUS,
  FONT_BODY,
  FONT_DISPLAY,
  GH,
  LIGHT,
  LIGHT_STATUS,
} from './ghColors';

// `createUnifiedTheme` shallow-merges `components` over `defaultComponentThemes`
// (`{ ...defaults, ...options.components }`), so any key we set replaces the
// default entirely. We spread the per-key defaults back in to keep Backstage's
// baseline (scrollbar styling, MuiCard flex column, etc.).
const dct = defaultComponentThemes as Record<string, any>;

/** Poppins for headings, Inter for everything else. */
const typography = {
  ...defaultTypography,
  fontFamily: FONT_BODY,
  h1: { ...defaultTypography.h1, fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { ...defaultTypography.h2, fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { ...defaultTypography.h3, fontFamily: FONT_DISPLAY, fontWeight: 600 },
  h4: { ...defaultTypography.h4, fontFamily: FONT_DISPLAY, fontWeight: 600 },
  h5: { ...defaultTypography.h5, fontFamily: FONT_DISPLAY, fontWeight: 600 },
  h6: { ...defaultTypography.h6, fontFamily: FONT_DISPLAY, fontWeight: 600 },
};

/** Page-type keys Backstage looks up for the header banner. */
const PAGE_KEYS = [
  'home', 'documentation', 'tool', 'service', 'website',
  'library', 'other', 'app', 'apis', 'card',
];

const pageThemeFor = (gradient: string, colors: string[], fontColor: string) =>
  Object.fromEntries(
    PAGE_KEYS.map(key => [
      key,
      { colors, shape: 'none', backgroundImage: gradient, fontColor },
    ]),
  );

/**
 * Component overrides. Kept to MUI primitives — the supported surface that
 * `transformV5ComponentThemesToV4` maps onto the v4 core-components. We omit
 * MuiCssBaseline entirely so Backstage's default (scrollbar/font/reset) is
 * preserved by the shallow merge. The page-header rule and body background are
 * handled in `ghGlobal.css` instead: the header is a Backstage component keyed
 * off a stable `BackstageHeader-*` class, and arbitrary global selectors don't
 * survive the v5→v4 CssBaseline transform.
 */
const componentsFor = (variant: 'light' | 'dark', border: string) => {
  const baseButton = dct.MuiButton?.styleOverrides ?? {};
  const baseCard = dct.MuiCard?.styleOverrides ?? {};

  return {
    MuiButton: {
      styleOverrides: {
        ...baseButton,
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
        // In light mode the primary hue is the brand yellow, which fails
        // contrast as text on white — so outlined/text buttons use ink. The
        // contained (CTA) button keeps yellow fill + ink text (brand-correct).
        ...(variant === 'light'
          ? {
              outlined: { color: GH.ink900, borderColor: GH.ink300 },
              text: { ...(baseButton as any).text, color: GH.ink900 },
            }
          : {}),
      },
    },
    MuiCard: {
      styleOverrides: {
        ...baseCard,
        root: {
          ...((baseCard as any).root ?? {}),
          borderRadius: 12,
          border: `1px solid ${border}`,
        },
      },
    },
  };
};

export const ghDarkTheme: UnifiedTheme = createUnifiedTheme({
  fontFamily: FONT_BODY,
  typography,
  defaultPageTheme: 'home',
  pageTheme: pageThemeFor(DARK.headerGradient, [...DARK.headerColors], DARK.textPrimary),
  palette: {
    ...palettes.dark,
    background: { default: DARK.bg, paper: DARK.paper },
    primary: { main: GH.yellow500, light: GH.yellow400, dark: GH.yellow600, contrastText: GH.ink900 },
    secondary: { main: GH.yellow400, contrastText: GH.ink900 },
    text: { ...(palettes.dark as any).text, primary: DARK.textPrimary, secondary: DARK.textSecondary },
    status: { ...palettes.dark.status, ...DARK_STATUS },
    error: { ...(palettes.dark as any).error, main: DARK_STATUS.error },
    warning: { ...(palettes.dark as any).warning, main: DARK_STATUS.warning },
    success: { ...(palettes.dark as any).success, main: DARK_STATUS.ok },
    link: GH.yellow400,
    linkHover: GH.yellow500,
    border: DARK.border,
    textSubtle: DARK.textSubtle,
    navigation: {
      ...palettes.dark.navigation,
      background: DARK.rail,
      indicator: GH.yellow500,
      color: DARK.navColor,
      selectedColor: GH.white,
      navItem: { hoverBackground: DARK.navHover },
      submenu: { background: DARK.submenu },
    },
  } as any,
  components: componentsFor('dark', DARK.border),
});

export const ghLightTheme: UnifiedTheme = createUnifiedTheme({
  fontFamily: FONT_BODY,
  typography,
  defaultPageTheme: 'home',
  pageTheme: pageThemeFor(LIGHT.headerGradient, [...LIGHT.headerColors], LIGHT.textPrimary),
  palette: {
    ...palettes.light,
    background: { default: LIGHT.bg, paper: LIGHT.paper },
    primary: { main: GH.yellow500, light: GH.yellow400, dark: GH.yellow600, contrastText: GH.ink900 },
    secondary: { main: GH.yellow600, contrastText: GH.white },
    text: { ...(palettes.light as any).text, primary: LIGHT.textPrimary, secondary: LIGHT.textSecondary },
    status: { ...palettes.light.status, ...LIGHT_STATUS },
    error: { ...(palettes.light as any).error, main: LIGHT_STATUS.error },
    warning: { ...(palettes.light as any).warning, main: LIGHT_STATUS.warning },
    success: { ...(palettes.light as any).success, main: LIGHT_STATUS.ok },
    // Ink link text (readable on white); brand yellow reserved for the underline/hover.
    link: GH.ink900,
    linkHover: GH.yellow600,
    border: LIGHT.border,
    textSubtle: LIGHT.textSubtle,
    navigation: {
      ...palettes.light.navigation,
      background: LIGHT.rail,
      indicator: GH.yellow500,
      color: LIGHT.navColor,
      selectedColor: GH.white,
      navItem: { hoverBackground: LIGHT.navHover },
      submenu: { background: LIGHT.submenu },
    },
  } as any,
  components: componentsFor('light', LIGHT.border),
});
